<template>
  <div class="row mt-4" v-if="evolucion.length">
    <div class="col-12">
      <div class="card ficha-listado">
        <div class="px-4 pb-4 card-body">

          <!-- Cabecera -->
          <div class="mb-3">
            <h6 class="mb-0 panel-section-title">Evolución de ventas e ingresos</h6>
            <p class="text-xs opacity-7 mb-0">Ventas directas y renovaciones Stripe mes a mes · se incluye el mes anterior al filtro para comparar</p>
          </div>

          <!-- KPIs del período -->
          <div class="row g-2 mb-3">
            <div class="col-6 col-md-3">
              <div class="evo-kpi">
                <div class="evo-kpi__k">Ventas {{ kpis.labelPeriodo }}</div>
                <div class="evo-kpi__v">{{ kpis.totalVentas }}</div>
                <div class="evo-kpi__sub">
                  <span class="evo-badge evo-badge--nueva">{{ kpis.totalNuevas }} nuevas</span>
                  <span v-if="kpis.totalRenov > 0" class="evo-badge evo-badge--renov">{{ kpis.totalRenov }} renovac.</span>
                </div>
              </div>
            </div>
            <div class="col-6 col-md-3">
              <div class="evo-kpi">
                <div class="evo-kpi__k">Ingresos EUR · {{ kpis.labelPeriodo }}</div>
                <div class="evo-kpi__v evo-kpi__v--naranja">{{ fmtEur(kpis.totalEur) }}</div>
                <div class="evo-kpi__trend" :class="trendClass(kpis.trendEur)">
                  {{ trendLabel(kpis.trendEur) }}<span v-if="kpis.mesPrevio"> vs {{ kpis.mesPrevio }}</span>
                </div>
              </div>
            </div>
            <div class="col-6 col-md-3">
              <div class="evo-kpi">
                <div class="evo-kpi__k">Ingresos USD · {{ kpis.labelPeriodo }}</div>
                <div class="evo-kpi__v evo-kpi__v--cyan">{{ fmtUsd(kpis.totalUsd) }}</div>
                <div class="evo-kpi__trend" :class="trendClass(kpis.trendUsd)">
                  {{ trendLabel(kpis.trendUsd) }}<span v-if="kpis.mesPrevio"> vs {{ kpis.mesPrevio }}</span>
                </div>
              </div>
            </div>
            <div class="col-6 col-md-3">
              <div class="evo-kpi">
                <div class="evo-kpi__k">Mejor mes</div>
                <div class="evo-kpi__v evo-kpi__v--sm">{{ kpis.mejorMes || '—' }}</div>
                <div class="evo-kpi__sub opacity-7">{{ kpis.mejorMesVentas }} ventas</div>
              </div>
            </div>
          </div>

          <!-- Gráfico -->
          <QuantyEvolucionChart
            id="chartEvolucion"
            :labels="chartData.labels"
            :ventas-nuevas="chartData.ventasNuevas"
            :ventas-renov="chartData.ventasRenov"
            :importe-eur="chartData.importeEur"
            :importe-usd="chartData.importeUsd"
          />

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QuantyEvolucionChart from "@/components/charts/QuantyEvolucionChart.vue";

const MESES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

function fmtMes(mesStr) {
  if (!mesStr) return mesStr;
  const [y, m] = mesStr.split("-");
  return `${MESES[parseInt(m) - 1]} '${String(y).slice(2)}`;
}

export default {
  name: "PanelTipsterEvolucion",
  components: { QuantyEvolucionChart },
  props: {
    evolucion:   { type: Array,  default: () => [] },
    fechaDesde:  { type: String, default: '' },
  },
  computed: {
    chartData() {
      return {
        labels:       this.evolucion.map(d => fmtMes(d.mes)),
        ventasNuevas: this.evolucion.map(d => d.ventas_nuevas ?? 0),
        ventasRenov:  this.evolucion.map(d => d.ventas_renov  ?? 0),
        importeEur:   this.evolucion.map(d => d.importe_eur   ?? 0),
        importeUsd:   this.evolucion.map(d => d.importe_usd   ?? 0),
      };
    },
    kpis() {
      const d = this.evolucion;

      // El backend siempre añade el mes anterior al filtro para comparación.
      // Los totales deben contar solo los meses dentro del rango elegido por el usuario.
      const mesMinFiltro = this.fechaDesde ? this.fechaDesde.slice(0, 7) : null;
      const enFiltro = mesMinFiltro
        ? d.filter(r => r.mes >= mesMinFiltro)
        : d;

      const totalNuevas = enFiltro.reduce((s, r) => s + (r.ventas_nuevas ?? 0), 0);
      const totalRenov  = enFiltro.reduce((s, r) => s + (r.ventas_renov  ?? 0), 0);
      const totalVentas = totalNuevas + totalRenov;
      const totalEur    = enFiltro.reduce((s, r) => s + (r.importe_eur   ?? 0), 0);
      const totalUsd    = enFiltro.reduce((s, r) => s + (r.importe_usd   ?? 0), 0);

      // Trend: compara el último mes del array con el penúltimo
      const last = d[d.length - 1];
      const prev = d[d.length - 2];
      const trendEur = (last && prev && (prev.importe_eur ?? 0) > 0)
        ? (((last.importe_eur ?? 0) - (prev.importe_eur ?? 0)) / (prev.importe_eur ?? 1)) * 100 : null;
      const trendUsd = (last && prev && (prev.importe_usd ?? 0) > 0)
        ? (((last.importe_usd ?? 0) - (prev.importe_usd ?? 0)) / (prev.importe_usd ?? 1)) * 100 : null;

      // Mejor mes entre todos los meses del gráfico (incluye el de comparación)
      const mejorMes = d.length
        ? d.reduce((best, r) => {
            const tot = (r.ventas_nuevas ?? 0) + (r.ventas_renov ?? 0);
            const bestTot = (best.ventas_nuevas ?? 0) + (best.ventas_renov ?? 0);
            return tot > bestTot ? r : best;
          }, d[0])
        : null;

      const labelPeriodo = enFiltro.length === 1
        ? fmtMes(enFiltro[0].mes)
        : enFiltro.length > 1
          ? `${fmtMes(enFiltro[0].mes)} – ${fmtMes(enFiltro[enFiltro.length - 1].mes)}`
          : 'período';

      return {
        totalVentas,
        totalNuevas,
        totalRenov,
        totalEur,
        totalUsd,
        trendEur,
        trendUsd,
        labelPeriodo,
        mesPrevio:      prev ? fmtMes(prev.mes) : null,
        mejorMes:       mejorMes ? fmtMes(mejorMes.mes) : null,
        mejorMesVentas: mejorMes ? (mejorMes.ventas_nuevas ?? 0) + (mejorMes.ventas_renov ?? 0) : 0,
      };
    },
  },
  methods: {
    fmtEur(n) {
      return Number(n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" });
    },
    fmtUsd(n) {
      return Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
    },
    trendClass(pct) {
      if (pct === null) return "opacity-5";
      return pct >= 0 ? "text-success" : "text-danger";
    },
    trendLabel(pct) {
      if (pct === null) return "Sin datos";
      const sign = pct >= 0 ? "+" : "";
      return `${sign}${pct.toFixed(1)}%`;
    },
  },
};
</script>

<style scoped>
/* KPIs del período */
.evo-kpi {
  background: var(--q-card-alt);
  border: 1px solid var(--q-border);
  border-radius: 12px;
  padding: 12px 14px;
  min-height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
}
.evo-kpi__k {
  font-size: 11px;
  color: var(--q-muted);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .4px;
}
.evo-kpi__v {
  font-size: 20px;
  font-weight: 800;
  color: var(--q-gold);
  line-height: 1.1;
  font-family: var(--q-font-mono);
  font-variant-numeric: tabular-nums;
}
.evo-kpi__v--naranja { color: #ff9800; }
.evo-kpi__v--cyan    { color: #26c6da; }
.evo-kpi__v--sm { font-size: 15px; }
.evo-kpi__trend {
  font-size: 11px;
  font-weight: 600;
  margin-top: 2px;
}
.evo-kpi__sub {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 2px;
}

/* Badges nuevas / renovaciones */
.evo-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 20px;
  line-height: 1.5;
}
.evo-badge--nueva {
  background: rgba(30,136,229,0.18);
  color: #5c9eff;
}
.evo-badge--renov {
  background: rgba(124,77,255,0.18);
  color: #b39ddb;
}
</style>
